import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../api/axiosInstance';
import { showNotification } from './notificationSlice';

const BASE = 'techsavanna_pos.api.attendance_api';

const ENDPOINTS = {
  clockIn: `${BASE}.clock_in`,
  clockOut: `${BASE}.clock_out`,
  getStatus: `${BASE}.get_attendance_status`,
  getLogs: `${BASE}.get_attendance_logs`,
  getSummary: `${BASE}.get_attendance_summary`,
};

const extract = (res) =>
  res.data?.message && typeof res.data.message === 'object'
    ? res.data.message
    : res.data?.message || res.data;

const extractErr = (err) => {
  const d = err.response?.data;
  if (d?._server_messages) {
    try {
      const msgs = JSON.parse(d._server_messages);
      const first = Array.isArray(msgs) ? msgs[0] : null;
      if (first) {
        const p = typeof first === 'string' ? JSON.parse(first) : first;
        if (p?.message) return String(p.message).replace(/<[^>]*>/g, '').trim();
      }
    } catch { /* fall through */ }
  }
  return d?.message || err.message || 'An error occurred';
};

export const clockIn = createAsyncThunk('attendance/clockIn', async (data = {}, { dispatch, rejectWithValue }) => {
  try {
    const res = await axiosInstance.post(ENDPOINTS.clockIn, data);
    const result = extract(res);
    dispatch(showNotification({ message: result.message || 'Clocked in', type: 'success' }));
    return result;
  } catch (err) {
    const msg = extractErr(err);
    dispatch(showNotification({ message: msg, type: 'error' }));
    return rejectWithValue(msg);
  }
});

export const clockOut = createAsyncThunk('attendance/clockOut', async (data = {}, { dispatch, rejectWithValue }) => {
  try {
    const res = await axiosInstance.post(ENDPOINTS.clockOut, data);
    const result = extract(res);
    dispatch(showNotification({ message: result.message || 'Clocked out', type: 'success' }));
    return result;
  } catch (err) {
    const msg = extractErr(err);
    dispatch(showNotification({ message: msg, type: 'error' }));
    return rejectWithValue(msg);
  }
});

export const getAttendanceStatus = createAsyncThunk('attendance/status', async (staffUser, { rejectWithValue }) => {
  try {
    const params = staffUser ? { staff_user: staffUser } : {};
    const res = await axiosInstance.get(ENDPOINTS.getStatus, { params });
    return extract(res);
  } catch (err) {
    return rejectWithValue(extractErr(err));
  }
});

export const getAttendanceLogs = createAsyncThunk('attendance/logs', async (params = {}, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(ENDPOINTS.getLogs, { params });
    return extract(res);
  } catch (err) {
    return rejectWithValue(extractErr(err));
  }
});

export const getAttendanceSummary = createAsyncThunk('attendance/summary', async (params = {}, { rejectWithValue }) => {
  try {
    const res = await axiosInstance.get(ENDPOINTS.getSummary, { params });
    return extract(res);
  } catch (err) {
    return rejectWithValue(extractErr(err));
  }
});

const attendanceSlice = createSlice({
  name: 'attendance',
  initialState: {
    clockedIn: false,
    currentAttendance: null,
    logs: [],
    summary: [],
    total: 0,
    loading: false,
    error: null,
  },
  reducers: { clearError: (state) => { state.error = null; } },
  extraReducers: (builder) => {
    const loading = (state) => { state.loading = true; state.error = null; };
    const failed = (state, action) => { state.loading = false; state.error = action.payload; };

    builder
      .addCase(clockIn.pending, loading)
      .addCase(clockIn.fulfilled, (state, action) => {
        state.loading = false;
        state.clockedIn = true;
        state.currentAttendance = action.payload?.attendance || null;
      })
      .addCase(clockIn.rejected, failed)
      .addCase(clockOut.pending, loading)
      .addCase(clockOut.fulfilled, (state, action) => {
        state.loading = false;
        state.clockedIn = false;
        state.currentAttendance = action.payload?.attendance || null;
      })
      .addCase(clockOut.rejected, failed)
      .addCase(getAttendanceStatus.fulfilled, (state, action) => {
        state.clockedIn = action.payload?.clocked_in || false;
        state.currentAttendance = action.payload?.attendance || null;
      })
      .addCase(getAttendanceLogs.pending, loading)
      .addCase(getAttendanceLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.logs = action.payload?.records || [];
        state.total = action.payload?.total || 0;
      })
      .addCase(getAttendanceLogs.rejected, failed)
      .addCase(getAttendanceSummary.pending, loading)
      .addCase(getAttendanceSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.summary = action.payload?.summary || [];
      })
      .addCase(getAttendanceSummary.rejected, failed);
  },
});

export const { clearError } = attendanceSlice.actions;
export default attendanceSlice.reducer;
